import { attention } from "micromark-core-commonmark";
import type { Construct, Event, Resolver, Token } from "micromark-util-types";
import type { Root, RootContent } from "mdast";
import type { Processor } from "unified";
import type { VFile } from "vfile";

declare module "micromark-util-types" {
	interface TokenTypeMap {
		deferredEmphasis: "deferredEmphasis";
	}
}

/** Resolve strong pairs before single stars can consume an incomplete pair. */
const resolveStrong: Resolver = (events, context) => {
	const deferred = new Set<Token>();
	const remaining = new Set<Token>();
	const first = events[0]?.[1].start;
	const last = events.at(-1)?.[1].end;
	for (const [kind, token] of events) {
		if (kind !== "enter" || token.type !== "attentionSequence") continue;
		const marker = context.sliceSerialize(token);
		if (marker.length === 1) {
			deferred.add(token);
			token.type = "deferredEmphasis";
		} else {
			remaining.add(token);
			if (marker === "**" && first && last) {
				const before = first.offset === token.start.offset ? "" : context.sliceSerialize({ start: first, end: token.start }, false);
				const after = token.end.offset === last.offset ? "" : context.sliceSerialize({ start: token.end, end: last }, false);
				if ((!token._open && !token._close) || !before || !after) {
					if (/^[ \t]+\S/.test(after)) token._open = true;
					if (/\S[ \t]+$/.test(before)) token._close = true;
				}
			}
		}
	}

	events = attention.resolveAll!(events, context);
	for (const token of deferred) token.type = "attentionSequence";
	for (const token of remaining) {
		if (token.type === "data" && (token.end.offset - token.start.offset === 1 || context.sliceSerialize(token).startsWith("_"))) token.type = "attentionSequence";
	}

	function resolveEmphasis(span: Event[]): Event[] {
		for (let i = 0; i < span.length; i++) {
			if (span[i]![0] !== "enter" || span[i]![1].type !== "strongText") continue;
			let end = i + 1;
			let depth = 1;
			for (; end < span.length; end++) {
				if (span[end]![1].type === "strongText") depth += span[end]![0] === "enter" ? 1 : -1;
				if (!depth) break;
			}
			const children = resolveEmphasis(span.slice(i + 1, end));
			span.splice(i + 1, end - i - 1, ...children);
			i += children.length + 1;
		}
		return attention.resolveAll!(span, context);
	}
	return resolveEmphasis(events);
};

const boundedAttention: Construct = { tokenize: attention.tokenize, resolveAll: resolveStrong };

/** Keep source offsets and protected tokens while loosening only horizontal bold whitespace. */
export function remarkBoundedStrong(this: Processor) {
	const data = this.data();
	(data.micromarkExtensions ??= []).push({
		text: { 42: boundedAttention, 95: boundedAttention },
		insideSpan: { null: [boundedAttention] },
	});
	return (tree: Root, file: VFile) => {
		const source = String(file.value);
		function walk(parent: Root | RootContent) {
			if (!("children" in parent)) return;
			for (let i = 0; i < parent.children.length; i++) {
				const node = parent.children[i]!;
				const raw = source.slice(node.position?.start.offset, node.position?.end.offset);
				if (node.type === "thematicBreak" && /^\*\*[ \t]+\*\*[ \t]*$/.test(raw)) {
					parent.children[i] = { type: "paragraph", children: [{ type: "text", value: raw }], position: node.position };
				} else if (node.type === "strong" && /^\*\*[ \t\r\n]*\*\*$/.test(raw)) {
					parent.children[i] = { type: "text", value: raw, position: node.position };
				} else {
					walk(node);
				}
			}
		}
		walk(tree);
	};
}
